<script>
import { reactive, ref, computed, toRefs } from 'vue'
import { useStore } from 'vuex'
import SvgIcon from '@/components/SvgIcon'
import SrUpload from '@/components/SrUpload'
import { routeTo } from "@/utils"
import { submitCompKycInfo, getCompKycInfo } from "@/api/modules/auth"
import RegStatus from './components/reg-status'
import ResetAuthTypeDialog from '@/components/ResetAuthTypeDialog'
import { vab } from "@libs/utils"
import auth from '@/compositions/modules/auth'
import { useI18n } from "@/plugins/i18n"

export default {
  components: { SvgIcon, SrUpload, RegStatus, ResetAuthTypeDialog },
  setup() {
    // 处理Form
    const initForm = () => {
      const { t } = useI18n()
      const store = useStore()
      const formReq = reactive({
        submitCompRegInfo: { // 注册人信息
          authFileId: "",
          backFileId: "",
          frontFileId: "",
          regCheckStatus: "",
          regName: "",
          regRejectRemark: "", // 驳回原因
          userType: "" // 注册人类型
        },
        submitCompInfo: { // 企业信息
          compAddr: "",
          compBrFileId: "",
          compCheckStatus: "",
          compCiFileId: "",
          compEAddr: "",
          compEname: "",
          compId: "",
          compName: "",
          compRegRegion: "",
          compRejectRemark: "",
          directorInfoList: [
            {
              backFileId: "",
              compUserId: "",
              frontFileId: "",
              idType: "",
              userCheckStatus: "",
              userRejectRemark: ""
            }
          ],
        },
        submitCompShareholderInfo: { // 股东信息
          shareholderInfoList: [
            {
              backFileId: "",
              compUserId: "",
              frontFileId: "",
              idType: "I",
              shareholderType: "",
              userCheckStatus: "",
              userRejectRemark: ""
            }
          ],
        },
        submitBusinessInfo: { // 业务信息
          employmentInfo: "",
          otherMaterialFileId: "",
          recoCustomerInfo: "",
          userId: ""
        },
      })

      const userTypeEnum = {
        L: t('auth.company.register.label.text_2'),
        A: t('auth.company.register.label.text_3'),
      }

      const idTypeEnum = {
        I: t('auth.company.info.content.text_15_2'),
        P: t('auth.company.info.content.text_15'),
        H: t('auth.company.info.content.text_15_4')
      }

      const loadingScope = reactive({
        pageLoading: false,
        btnLoading: false
      })

      const judgeRejStatus = () => {
        return ['RJ', 'RC', 'ST'].includes(formReq.submitCompRegInfo.regCheckStatus) ||
          ['RJ', 'RC', 'ST'].includes(formReq.submitCompInfo.compCheckStatus) ||
          formReq.submitCompInfo.directorInfoList.some(item => ['RJ', 'RC', 'ST'].includes(item.userCheckStatus)) ||
          formReq.submitCompShareholderInfo.shareholderInfoList.some(item => ['RJ', 'RC', 'ST'].includes(item.userCheckStatus))
      }

      const initData = async () => {
        loadingScope.pageLoading = true
        try {
          const res = await getCompKycInfo()
          store.dispatch('auth/setStep')

          formReq.submitCompRegInfo = res.submitCompRegInfo
          formReq.submitCompInfo = res.submitCompInfo
          formReq.submitCompShareholderInfo = res.submitCompShareholderInfo
          formReq.submitBusinessInfo = res.submitBusinessInfo

          loadingScope.pageLoading = false
        } catch {
          loadingScope.pageLoading = false
        }
      }

      initData()

      return { formReq, loadingScope, userTypeEnum, idTypeEnum, judgeRejStatus }
    }

    // 处理弹框
    const initDialog = () => {
      const dialogVisible = ref(false)
      return { dialogVisible }
    }

    const store = useStore()
    const { FILE } = store.state.base.constant
    const _initForm = initForm()
    const { t, locale } = useI18n()
    const state = reactive({
      compRegRegionEnum: store.state.base.dict.country.map,
    })

    const COMP_FILE_ENUM = reactive({
      CN: {
        file_1: require("@/assets/img/components/sr-upload/ex/cn.jpg"),
        file_1_btn_desc: t("auth.company.info.content.upload"),
        file_1_down_desc: t("auth.company.info.content.upload2"),
        file_1_require_desc: t("auth.company.info.error.upload"),

      },
      HK: {
        file_1: require("@/assets/img/components/sr-upload/ex/hk_br.jpg"),
        file_1_btn_desc: t("auth.company.info.content.uploadBR"),
        file_1_down_desc: t("auth.company.info.content.uploadBR2"),
        file_1_require_desc: t("auth.company.info.error.uploadBR"),

        file_2: require("@/assets/img/components/sr-upload/ex/hk_ci.jpg"),
        file_2_btn_desc: t("auth.company.info.content.uploadCI"),
        file_2_down_desc: t("auth.company.info.content.uploadCI2"),
        file_2_require_desc: t("auth.company.info.error.uploadCI"),
      },
      SG: {
        file_1: require("@/assets/img/components/sr-upload/ex/singapore.jpg"),
        file_1_btn_desc: t("auth.company.info.content.uploadACRA"),
        file_1_down_desc: t("auth.company.info.content.uploadACRA2"),
        file_1_require_desc: t("auth.company.info.error.ACRA"),
      },
      GB: {
        file_1: require("@/assets/img/components/sr-upload/ex/england.jpg"),
        file_1_btn_desc: t("auth.company.info.content.uploadCert"),
        file_1_down_desc: t("auth.company.info.content.uploadCert2"),
        file_1_require_desc: t("auth.company.info.error.otherCert"),
      },
      ID: {
        file_1: require("@/assets/img/components/sr-upload/ex/id-license.png"),
        file_1_btn_desc: t("auth.company.info.content.uploadNib"),
        file_1_down_desc: t("auth.company.info.content.uploadNib2"),
        file_1_require_desc: t("auth.company.info.error.uploadNib"),
      },
      OTHER: {
        file_1: "",
        file_1_btn_desc: t("auth.company.info.content.uploadCert"),
        file_1_down_desc: t("auth.company.info.content.uploadCert2"),
        file_1_require_desc: t("auth.company.info.error.otherCert"),
      }
    })

    const scopedMethods = {
      handleEditable: () => ((scopedComputed.kycReviewStatus.value && ['WC', 'RJ'].includes(scopedComputed.kycReviewStatus.value)) || !scopedComputed.kycReviewStatus.value),
      handleUpdateStep: (step) => {
        switch (step) {
          case 'submitCompRegInfo':
            routeTo('/auth/company/register')
            break
          case 'submitCompInfo':
            routeTo('/auth/company/info')
            break
          case 'submitCompShareholderInfo':
            routeTo('/auth/company/shareholder')
            break
          case 'submitBusinessInfo':
            routeTo('/auth/company/businessInfo')
            break
          default:
            break
        }
      },
      // 显示提交按钮
      handleShowOperation: auth.handleShowOperation,
      // 提交所有
      handleSubmitAll: async () => {
        if (!scopedMethods.handleShowOperation()) return routeTo('/dashboard')
        _initForm.loadingScope.btnLoading = true
        try {
          // await store.dispatch('user/getRegisterInfo')
          const params = {}
          await submitCompKycInfo(params)
          vab.baseMessage('提交成功', 'success')
          routeTo('/dashboard')
          _initForm.loadingScope.btnLoading = false
        } catch (err) {
          _initForm.loadingScope.btnLoading = false
          console.log(err)
        }
      }
    }

    const scopedComputed = {
      stepComplete: computed(() => store.state.auth.registerInfo.stepComplete),
      kycReviewStatus: computed(() => store.state.auth.registerInfo.kycReviewStatus),
      countryDesc: computed(() => {
        return COMP_FILE_ENUM?.[_initForm.formReq.submitCompInfo.compRegRegion] || COMP_FILE_ENUM.OTHER
      })
    }

    return {
      FILE,
      ...toRefs(initDialog()),
      ...scopedComputed,
      ..._initForm,
      COMP_FILE_ENUM,
      ...state,
      t,
      ...scopedMethods,
    }
  }
}


</script>

<template>
  <div class="company-auth-overview-container">
    <el-row>
      <div class="block-one">
        <div class="h0">{{ t('auth.company.overview.title.text_1') }}</div>
        <div class="a" style="font-size: 16px;margin-right: 6px;" v-if="handleShowOperation()"
          @click="dialogVisible = true">{{ t('auth.personal.register.content.text_7') }}<svg-icon icon-class="问号" /></div>
      </div>
      <div class="reject-wrap" v-if="judgeRejStatus()">
        <svg-icon icon-class="红色叹号" class-name="icon" />
        <div style="margin-left: 14px;">{{ t('dashboard.bank.content.text_4') }}</div>
        <div style="margin-left: 78px;">{{ t('auth.company.overview.content.text_3') }}</div>
      </div>
    </el-row>
    <el-row>
      <el-col :span="24">
        <el-form :model="formReq" label-width="150px" label-position="left">
          <!--注册人信息-->
          <div class="form-operation-wrap">
            <div class="title">{{ t('auth.company.register.title.text_1') }}</div>
            <div class="btn"
              v-if="['RJ', 'RC', 'ST', 'WC'].includes(formReq.submitCompRegInfo.regCheckStatus) && handleEditable()"
              @click="handleUpdateStep('submitCompRegInfo')">{{ t('auth.company.overview.content.text_1') }}</div>
          </div>
          <div class="content-wrap">
            <!--审核状态-->
            <reg-status :checkStatus="formReq.submitCompRegInfo.regCheckStatus"
              :rejectRemark="formReq.submitCompRegInfo.regRejectRemark" />

            <el-form-item prop="compName" :label="t('auth.company.register.label.text_1')">
              <span>{{ userTypeEnum[formReq.submitCompRegInfo.userType] }}</span>
            </el-form-item>

            <el-form-item
              :label="formReq.submitCompRegInfo.userType === 'L' ? t('auth.company.register.label.text_4') : t('auth.company.register.label.text_5')">
              <span>{{ formReq.submitCompRegInfo.regName }}</span>
            </el-form-item>

            <!--证件签发地-->
            <el-form-item :label="t('auth.company.register.label.issuingCountry')"
              v-if="formReq.submitCompRegInfo.userType === 'A'">
              <span>{{ compRegRegionEnum[formReq.submitCompRegInfo.countryCode] || '-' }}</span>
            </el-form-item>

            <el-form-item :label="t('auth.company.register.label.text_6')"
              v-if="formReq.submitCompRegInfo.userType === 'A'">

              <div class="auth-upload-container">
                <div class="h2" style="height: 10px;"></div>
                <div class="auth-upload-wrap">
                  <div class="auth-upload">
                    <sr-upload v-model:fileId="formReq.submitCompRegInfo.authFileId" :filePrefix="FILE.PREFIX.AUTH_FILE"
                      :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3 ta-c">{{ t('auth.company.register.label.text_6') }}</div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item :label="t('auth.personal.register.label.text_4')"
              v-if="formReq.submitCompRegInfo.userType === 'A'">
              <div class="auth-upload-container">

                <div class="upload-title-wrap">
                  <div class="title" style="margin-bottom: 16px;height:40px;line-height: 40px;">
                    {{ idTypeEnum[formReq.submitCompRegInfo.idType] }}</div>
                </div>
                <div class="auth-upload-wrap">
                  <div class="auth-upload">
                    <sr-upload v-model:fileId="formReq.submitCompRegInfo.frontFileId"
                      :filePrefix="FILE.PREFIX.FRONT_FILE" :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3 ta-c" v-if="formReq.submitCompRegInfo.idType === 'P'">
                      {{ t('auth.company.info.content.text_24') }}</div>
                    <div class="h3 ta-c" v-else>{{ t('auth.company.register.placeholder.text_6') }}</div>
                  </div>
                  <div class="auth-upload" v-if="formReq.submitCompRegInfo.idType === 'I'">
                    <sr-upload v-model:fileId="formReq.submitCompRegInfo.backFileId" :filePrefix="FILE.PREFIX.BACK_FILE"
                      :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3 ta-c">{{ t('auth.company.register.placeholder.text_7') }}</div>
                  </div>
                </div>
              </div>
            </el-form-item>
          </div>

          <!--企业信息-->
          <div class="form-operation-wrap">
            <div class="title">{{ t('auth.company.info.title.text_1') }}</div>
            <div class="btn"
              v-if="((['RJ', 'RC', 'ST', 'WC'].includes(formReq.submitCompInfo.compCheckStatus) || formReq.submitCompInfo.directorInfoList.some(directorItem => ['RJ', 'RC', 'ST', 'WC'].includes(directorItem.userCheckStatus))) && handleEditable())"
              @click="handleUpdateStep('submitCompInfo')">{{ t('auth.company.overview.content.text_1') }}</div>
          </div>
          <div class="content-wrap">
            <!--审核状态-->
            <reg-status :checkStatus="formReq.submitCompInfo.compCheckStatus"
              :rejectRemark="formReq.submitCompInfo.compRejectRemark" />

            <el-row>
              <el-col :span="12">
                <el-form-item prop="compName" :label="t('auth.company.info.label.text_1')">
                  <span>{{ formReq.submitCompInfo.compName }}</span>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="compName" :label="t('auth.company.info.label.text_2')">
                  <span>{{ formReq.submitCompInfo.compEname }}</span>
                </el-form-item>
              </el-col>
            </el-row>

            <el-form-item prop="compName" :label="t('auth.company.info.label.text_3')">
              <span>{{ compRegRegionEnum[formReq.submitCompInfo.compRegRegion] || '-' }}</span>
            </el-form-item>

            <el-row>
              <el-col :span="12">
                <el-form-item prop="compName" :label="t('auth.company.info.label.text_4')">
                  <span>{{ formReq.submitCompInfo.compAddr }}</span>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="compName" :label="t('auth.company.info.label.text_5')">
                  <span>{{ formReq.submitCompInfo.compEAddr }}</span>
                </el-form-item>
              </el-col>
            </el-row>

            <el-form-item :label="t('auth.company.info.label.text_7')">
              <div class="auth-upload-container">
                <div class="h2" style="height: 10px;"></div>
                <div class="auth-upload-wrap">
                  <div class="auth-upload">
                    <sr-upload v-model:fileId="formReq.submitCompInfo.compBrFileId" :filePrefix="FILE.PREFIX.AUTH_FILE"
                      :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3">{{ countryDesc?.file_1_down_desc || '-' }}</div>
                  </div>
                  <div class="auth-upload" v-if="formReq.submitCompInfo.compRegRegion === 'HK'">
                    <sr-upload v-model:fileId="formReq.submitCompInfo.compCiFileId" :filePrefix="FILE.PREFIX.AUTH_FILE"
                      :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3">{{ countryDesc?.file_2_down_desc || '-' }}</div>
                  </div>
                  <div class="auth-upload" v-if="!['CN', 'TW', 'MO', 'HK'].includes(formReq.submitCompInfo.compRegRegion)">
                    <sr-upload v-model:fileId="formReq.submitCompInfo.compCiFileId" :filePrefix="FILE.PREFIX.AUTH_FILE"
                      :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3">{{ t('auth.company.info.content.uploadCO') }}</div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <div class="card-container"
              v-for="(item, index) in formReq.submitCompInfo.directorInfoList?.filter(i => i.userType == 'L')" :key="index">
              <!--审核状态-->
              <reg-status :checkStatus="item.userCheckStatus" :rejectRemark="item.userRejectRemark" />
              <div class="h1">
                <span
                  v-if="formReq.submitCompInfo.compRegRegion == 'CN'">{{ t('auth.company.info.content.text_14') }}</span>
                <span v-else>{{ t('auth.company.info.content.text_1') }}{{ index + 1 }}</span>
              </div>
              <!--证件签发地-->
              <el-form-item :label="t('auth.company.register.label.issuingCountry')">
                <span>{{ compRegRegionEnum[item.countryCode] || '-' }}</span>
              </el-form-item>
              <el-form-item :label="t('auth.company.info.label.text_6')">
                <div class="auth-upload-container">
                  <div class="upload-title-wrap">
                    <div class="title" style="margin-bottom: 16px;height:40px;line-height: 40px;">
                      {{ idTypeEnum[item.idType] }}</div>
                  </div>
                  <div class="auth-upload-wrap">
                    <div class="auth-upload">
                      <sr-upload v-model:fileId="item.frontFileId" :filePrefix="FILE.PREFIX.FRONT_FILE"
                        :mode="FILE.UPLOAD_MODE.VIEW" />
                      <div class="h3 ta-c" v-if="item.idType === 'P'">{{ t('auth.company.info.content.text_24') }}</div>
                      <div class="h3 ta-c" v-else>{{ t('auth.company.register.placeholder.text_6') }}</div>
                    </div>
                    <div class="auth-upload" v-if="item.idType !== 'P'">
                      <sr-upload v-model:fileId="item.backFileId" :filePrefix="FILE.PREFIX.BACK_FILE"
                        :mode="FILE.UPLOAD_MODE.VIEW" />
                      <div class="h3 ta-c">{{ t('auth.company.register.placeholder.text_7') }}</div>
                    </div>
                  </div>
                </div>
              </el-form-item>
            </div>
            <!-- 监事信息 -->
            <div class="card-container"
              v-for="(item, index) in formReq.submitCompInfo.directorInfoList?.filter(i => i.userType == 'C')" :key="index">
              <!--审核状态-->
              <reg-status :checkStatus="item.userCheckStatus" :rejectRemark="item.userRejectRemark" />
              <div class="h1">{{ t('auth.company.info.content.text_22') }}{{ index + 1 }}</div>
              <!--证件签发地-->
              <el-form-item :label="t('auth.company.register.label.issuingCountry')">
                <span>{{ compRegRegionEnum[item.countryCode] || '-' }}</span>
              </el-form-item>
              <el-form-item :label="t('auth.company.info.label.text_6')">
                <div class="auth-upload-container">
                  <div class="upload-title-wrap">
                    <div class="title" style="margin-bottom: 16px;height:40px;line-height: 40px;">
                      {{ idTypeEnum[item.idType] }}</div>
                  </div>
                  <div class="auth-upload-wrap">
                    <div class="auth-upload">
                      <sr-upload v-model:fileId="item.frontFileId" :filePrefix="FILE.PREFIX.FRONT_FILE"
                        :mode="FILE.UPLOAD_MODE.VIEW" />
                      <div class="h3 ta-c" v-if="item.idType === 'P'">{{ t('auth.company.info.content.text_24') }}</div>
                      <div class="h3 ta-c" v-else>{{ t('auth.company.register.placeholder.text_6') }}</div>
                    </div>
                    <div class="auth-upload" v-if="item.idType !== 'P'">
                      <sr-upload v-model:fileId="item.backFileId" :filePrefix="FILE.PREFIX.BACK_FILE"
                        :mode="FILE.UPLOAD_MODE.VIEW" />
                      <div class="h3 ta-c">{{ t('auth.company.register.placeholder.text_7') }}</div>
                    </div>
                  </div>
                </div>
              </el-form-item>
            </div>
          </div>

          <!--股东信息-->
          <div class="form-operation-wrap">
            <div class="title">{{ t('auth.company.shareholder.title.text_1') }}</div>
            <div class="btn"
              v-if="formReq.submitCompShareholderInfo.shareholderInfoList.some(shareholderItem => ['RJ', 'RC', 'ST', 'WC'].includes(shareholderItem.userCheckStatus)) && handleEditable()"
              @click="handleUpdateStep('submitCompShareholderInfo')">{{ t('auth.company.overview.content.text_1') }}</div>
          </div>
          <div class="content-wrap">
            <div class="card-container" v-for="(item, index) in formReq.submitCompShareholderInfo.shareholderInfoList"
              :key="index">
              <!--审核状态-->
              <reg-status :checkStatus="item.userCheckStatus" :rejectRemark="item.userRejectRemark" />
              <div class="h1">{{ t('auth.company.info.content.text_19') }}{{ index + 1 }}</div>
              <!--证件签发地-->
              <el-form-item :label="t('auth.company.register.label.issuingCountry')">
                <span>{{ compRegRegionEnum[item.countryCode] || '-' }}</span>
              </el-form-item>
              <el-form-item :label="t('auth.company.info.label.text_6')">
                <div class="auth-upload-container">
                  <div class="upload-title-wrap">
                    <div class="title" style="margin-bottom: 16px;height:40px;line-height: 40px;">
                      {{ idTypeEnum[item.idType] }}</div>
                  </div>
                  <div class="auth-upload-wrap">
                    <div class="auth-upload">
                      <sr-upload v-model:fileId="item.frontFileId" :filePrefix="FILE.PREFIX.FRONT_FILE"
                        :mode="FILE.UPLOAD_MODE.VIEW" />
                      <div class="h3 ta-c" v-if="item.idType === 'P'">{{ t('auth.company.info.content.text_24') }}</div>
                      <div class="h3 ta-c" v-else>{{ t('auth.company.register.placeholder.text_6') }}</div>
                    </div>
                    <div class="auth-upload" v-if="item.idType !== 'P'">
                      <sr-upload v-model:fileId="item.backFileId" :filePrefix="FILE.PREFIX.BACK_FILE"
                        :mode="FILE.UPLOAD_MODE.VIEW" />
                      <div class="h3 ta-c">{{ t('auth.company.register.placeholder.text_7') }}</div>
                    </div>
                  </div>
                </div>
              </el-form-item>
            </div>
          </div>

          <!--业务信息-->
          <div class="form-operation-wrap">
            <div class="title">{{ t('auth.company.business.title.text_1') }}</div>
            <div class="btn" v-if="+stepComplete !== 1 && handleEditable()"
              @click="handleUpdateStep('submitBusinessInfo')">{{ t('auth.company.overview.content.text_1') }}</div>
          </div>
          <div class="content-wrap">
            <!--审核状态-->
            <!--<reg-status :scopeData="formReq.submitBusinessInfo" />-->
            <el-form-item prop="compName" :label="t('auth.company.business.label.text_1')">
              <span>{{ formReq.submitBusinessInfo.employmentInfo }}</span>
            </el-form-item>

            <el-form-item :label="t('auth.company.business.label.text_2')">
              <span>{{ formReq.submitBusinessInfo.recoCustomerInfo }}</span>
            </el-form-item>

            <el-form-item :label="t('auth.company.business.label.text_5')">
              <div class="auth-upload-container">
                <div class="h2" style="height: 10px;"></div>
                <div class="auth-upload-wrap">
                  <div class="auth-upload">
                    <sr-upload v-model:fileId="formReq.submitBusinessInfo.otherMaterialFileId"
                      :filePrefix="FILE.PREFIX.OTHER_MATERIAL" :mode="FILE.UPLOAD_MODE.VIEW" />
                    <div class="h3 ta-c">{{ t('auth.company.business.placeholder.text_1') }}</div>
                  </div>
                </div>
              </div>
            </el-form-item>
          </div>
        </el-form>
      </el-col>
    </el-row>
    <div class="submit" @click="handleSubmitAll">
      {{ handleShowOperation() ? t('auth.company.overview.content.text_2') : t('auth.company.overview.content.text_4') }}
    </div>
    <reset-auth-type-dialog :viewVisible="dialogVisible" @close="dialogVisible = false" />
  </div>
</template>

<style scoped lang="scss">
.company-auth-overview-container {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 200px;
  position: relative;

  .reject-wrap {
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    padding: 0 20px;
    border-radius: 4px;
    font-size: 14px;
    color: #E15C46;
    letter-spacing: 0;
    line-height: 26px;
    background: #FFF3F3;
    margin-bottom: 30px;

    .icon {
      font-size: 20px;
    }
  }

  .content-wrap {
    width: 100%;
    box-sizing: border-box;
    padding-left: 21px;

    span {
      word-break: break-all;
    }

    .card-container {
      width: 100%;

      .title {
        font-size: 14px;
        font-weight: 400;
        color: #677280;
        line-height: 21px;
        margin-bottom: 16px;
      }
    }
  }

  .submit {
    /*position: fixed;*/
    width: 272px;
    height: 48px;
    background: linear-gradient(327deg, #39B7FF 0%, #409EFF 100%);
    box-shadow: 0 13px 44px 12px rgba(64, 158, 255, 0.26);
    border-radius: 6px;
    /*bottom: 82px;*/
    /*left: 50%;*/
    /*transform: translateX(-50%);*/
    margin: 70px auto;
    font-size: 16px;
    font-weight: 400;
    color: #FFFFFF;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .auth-upload-container {
    width: 100%;
    // padding-top: 10px;

    .upload-title-wrap {
      width: 100%;
    }

    .auth-upload-wrap {
      display: flex;

      .auth-upload {
        margin-right: 60px;
      }
    }
  }

  .form-operation-wrap {
    width: 100%;
    padding: 13px 20px;
    background: linear-gradient(270deg, rgba(237, 245, 253, 0) 0%, rgba(233, 236, 238, 0.58) 100%);
    font-size: 16px;
    font-weight: 600;
    color: #002D4F;
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 40px;

    .btn {
      padding: 8px 17px;
      background: #E0EFFF;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 400;
      color: #409EFF;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
    }
  }

  .block-one {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 40px 0;
  }

  .ta-c {
    text-align: center;
  }

  :deep(.el-form-item__content) {
    flex: inherit !important;
    width: auto !important;

    .el-input__inner,
    .el-textarea__inner {
      width: 360px !important;
      border-radius: 4px !important;
    }

    .el-input {
      width: auto;
    }
  }

}
</style>
